import { useEffect, useState } from 'react';
import { Package, Loader2, CheckCircle, AlertCircle, Calendar, Hash, ExternalLink } from 'lucide-react';
import { purchasingAPI, NcrRef, POLineGRNSummary, POLineGRNSummaryRow } from '../../lib/api';
import { toast } from 'sonner';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useColumnSort } from '../../hooks/useColumnSort';
import { SortableTh } from '../common/SortableTh';

interface POLineGRNTabProps {
  lineId: number;
  /** CR-4698: Total ordered quantity from the PO line, used for Outstanding calculation. */
  orderedQty?: number;
}

/**
 * Customer review 11 Sep (item 30c): the tooltip on an NCR-derived
 * rejection — "Includes 1 from NCR 3252".
 */
const ncrTooltip = (qty: string | undefined, refs: NcrRef[] | undefined): string | undefined => {
  if (!refs?.length || !(parseFloat(qty ?? '') > 0)) return undefined;
  return `Includes ${parseFloat(qty ?? '')} from ${refs.map((r) => `NCR ${r.ncr_number}`).join(', ')}`;
};

/** The NCR-inclusive figures, falling back to the stored ones on a payload without them. */
const acceptedOf = (g: POLineGRNSummaryRow) => g.effective_accepted_qty ?? g.quantity_accepted;
const rejectedOf = (g: POLineGRNSummaryRow) => g.effective_rejected_qty ?? g.quantity_rejected;
/** Module scope: the sort hook memoises on this object's identity (CR-7263). */
const GRN_SORT_KEYS = {
  effective_accepted_qty: acceptedOf,
  effective_rejected_qty: rejectedOf,
} as const;

export default function POLineGRNTab({ lineId, orderedQty = 0 }: POLineGRNTabProps) {
  const [summary, setSummary] = useState<POLineGRNSummary | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const openGRN = (grnNumber: string) => {
    window.open(`/purchasing?tab=delivery&search=${encodeURIComponent(grnNumber)}`, '_blank');
  };

  // DORMANT (CR-7256). Reachable only from a GRN number containing a hyphen.
  // It assumes numbers are formatted <document>-<suffix>, so several GRN lines
  // on one delivery share a document prefix and searching the prefix widens the
  // view from one line to the whole delivery. Where that format is not used,
  // split('-')[0] returns the whole number and this builds the SAME url as
  // openGRN above — which is what made the button look broken: two controls,
  // one destination. Kept rather than deleted because a tenant using the
  // format would lose a control that does a real job; see CR-7256 for the
  // survey that decides whether it goes for good.
  const openAllOrdersOnGRN = (grnNumber: string) => {
    const docNumber = grnNumber.split('-')[0];
    window.open(`/purchasing?tab=delivery&search=${encodeURIComponent(docNumber)}`, '_blank');
  };

  useEffect(() => {
    fetchGRNSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lineId]);

  const fetchGRNSummary = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await purchasingAPI.getLineGRNSummary(lineId);
      setSummary(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch GRN summary:', err);
      setError(getApiErrorMessage(err, 'Failed to load GRN summary'));
      toast.error('Failed to load GRN summary');
    } finally {
      setLoading(false);
    }
  };

  // CR-7263: the GRNs of one PO line, fully loaded and unpaginated, so
  // sorting the array is the whole answer -- no ordering parameter, per
  // R-056/R-057. Declared here, above the loading/error early returns: a hook
  // below one of those is called on some renders and not others.
  // Customer review 11 Sep (item 30c/e): Accepted / Rejected sort on the
  // NCR-inclusive figures they print; the Inspection Status column is gone —
  // inspection is not a platform concept (msg 5e, 30e).
  const { sortedRows: sortedGrns, sortField, sortDirection, onSort } = useColumnSort(
    summary?.grns ?? [],
    {
      numericFields: ['schedule_line_number', 'quantity_received', 'effective_accepted_qty', 'effective_rejected_qty'],
      dateFields: ['receipt_date'],
      computed: GRN_SORT_KEYS,
    },
  );

  // CR-7265: Sched Line is a schedule-grain column. Tenants without the
  // module have no schedule lines at all, so the column is a dash on every
  // row -- and one whole column of dashes reads as broken data. The header
  // and the cell share this single flag so they cannot drift out of step and
  // shift every column after them by one.
  const { isFeatureEnabled } = useMenuSettings();
  const showScheduleLine = isFeatureEnabled('po_line_schedules');

  const { formatDate: contextFormatDate, formatQuantity } = useFormatters();

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return contextFormatDate(dateString);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <p className="text-red-600 font-medium">{error}</p>
        <button
          onClick={fetchGRNSummary}
          className="mt-4 px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary/90"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!summary || summary.total_grns === 0) {
    return (
      <div className="text-center py-12">
        <Package className="h-12 w-12 text-muted-foreground mx-auto mb-4" />
        <p className="text-muted-foreground font-medium">No GRNs found for this PO line</p>
        <p className="text-sm text-muted-foreground mt-2">
          Goods Receipt Notes will appear here once deliveries are recorded
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* CR-4698: Summary Statistics — Total Ordered, Total Received, Outstanding */}
      <div className="grid grid-cols-3 gap-4">
        <div className="bg-gradient-to-br from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-blue-900">Total Ordered</span>
            <Package className="h-5 w-5 text-blue-600" />
          </div>
          <p className="text-2xl font-bold text-blue-900">{formatQuantity(orderedQty)}</p>
        </div>

        <div className="bg-gradient-to-br from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-green-900">Total Received</span>
            <CheckCircle className="h-5 w-5 text-green-600" />
          </div>
          <p className="text-2xl font-bold text-green-900">{formatQuantity(summary.total_received)}</p>
        </div>

        <div className="bg-gradient-to-br from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-orange-900">Outstanding Qty</span>
            <AlertCircle className="h-5 w-5 text-orange-600" />
          </div>
          <p className="text-2xl font-bold text-orange-900">{formatQuantity(Math.max(0, orderedQty - parseFloat(summary.total_received)))}</p>
        </div>
      </div>

      {/* GRN List */}
      <div>
        <h3 className="text-lg font-semibold text-primary mb-4">Delivery History</h3>
        <div className="border rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-muted/50 border-b">
              <tr>
                <SortableTh field="grn_number" sortField={sortField} sortDirection={sortDirection}
                  onSort={onSort} align="left"
                  className="px-4 py-3 text-sm font-semibold text-muted-foreground">GRN Number</SortableTh>
                <SortableTh field="receipt_date" sortField={sortField} sortDirection={sortDirection}
                  onSort={onSort} align="left"
                  className="px-4 py-3 text-sm font-semibold text-muted-foreground">Receipt Date</SortableTh>
                {showScheduleLine && (
                  <SortableTh field="schedule_line_number" sortField={sortField} sortDirection={sortDirection}
                    onSort={onSort} align="right"
                    className="px-4 py-3 text-sm font-semibold text-muted-foreground">Sched Line</SortableTh>
                )}
                <SortableTh field="quantity_received" sortField={sortField} sortDirection={sortDirection}
                  onSort={onSort} align="right"
                  className="px-4 py-3 text-sm font-semibold text-muted-foreground">Qty Received</SortableTh>
                <SortableTh field="effective_accepted_qty" sortField={sortField} sortDirection={sortDirection}
                  onSort={onSort} align="right"
                  className="px-4 py-3 text-sm font-semibold text-muted-foreground">Qty Accepted</SortableTh>
                <SortableTh field="effective_rejected_qty" sortField={sortField} sortDirection={sortDirection}
                  onSort={onSort} align="right"
                  className="px-4 py-3 text-sm font-semibold text-muted-foreground">Qty Rejected</SortableTh>
                <SortableTh field="packing_slip_number" sortField={sortField} sortDirection={sortDirection}
                  onSort={onSort} align="left"
                  className="px-4 py-3 text-sm font-semibold text-muted-foreground">Packing Slip</SortableTh>
              </tr>
            </thead>
            <tbody className="divide-y">
              {sortedGrns.map((grn, index) => (
                <tr
                  key={grn.id}
                  className={`hover:bg-muted/30 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-muted/10'}`}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Hash className="h-4 w-4 text-muted-foreground" />
                      <button
                        onClick={() => openGRN(grn.grn_number)}
                        className="font-medium text-primary hover:underline inline-flex items-center gap-1"
                        title="View this GRN on Deliveries page"
                      >
                        {grn.grn_number}
                        <ExternalLink className="h-3 w-3" />
                      </button>
                      {/* CR-7256: only offer "View All" when the number has a
                          suffix to strip. Without one it searches the same term
                          as the number beside it and opens an identical page —
                          the redundant second control reported on the staging
                          walkthrough. */}
                      {grn.grn_number.includes('-') && (
                        <button
                          onClick={() => openAllOrdersOnGRN(grn.grn_number)}
                          className="px-2 py-0.5 text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded hover:bg-blue-100 transition-colors"
                          title="View all lines on this delivery"
                        >
                          View All
                        </button>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <Calendar className="h-4 w-4 text-muted-foreground" />
                      <span className="text-sm">{formatDate(grn.receipt_date)}</span>
                    </div>
                  </td>
                  {showScheduleLine && (
                    <td className="px-4 py-3 text-right text-sm">{grn.schedule_line_number ?? '-'}</td>
                  )}
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium">{formatQuantity(grn.quantity_received)}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="font-medium text-green-700">{formatQuantity(acceptedOf(grn))}</span>
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span
                      className={`font-medium text-red-700 ${grn.ncr_refs?.length ? 'underline decoration-dotted cursor-help' : ''}`}
                      title={ncrTooltip(grn.ncr_rejected_qty, grn.ncr_refs)}
                    >
                      {formatQuantity(rejectedOf(grn))}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className="text-sm text-muted-foreground">
                      {grn.packing_slip_number || '-'}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Acceptance Rate Summary */}
      {summary.total_grns > 0 && parseFloat(summary.total_received) > 0 && (
        <div className="bg-muted/30 border rounded-lg p-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Acceptance Rate</p>
              <p className="text-xl font-bold text-primary">
                {((parseFloat(summary.total_accepted) / parseFloat(summary.total_received)) * 100).toFixed(1)}%
              </p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground mb-1">Rejection Rate</p>
              <p
                className="text-xl font-bold text-red-600"
                title={parseFloat(summary.ncr_rejected) > 0 ? `Includes ${parseFloat(summary.ncr_rejected)} rejected on NCRs` : undefined}
              >
                {((parseFloat(summary.total_rejected) / parseFloat(summary.total_received)) * 100).toFixed(1)}%
              </p>
            </div>
          </div>
          {/* Customer review 11 Sep (item 30c): an NCR that names no
              receipt of this line still rejects parts of it — it is in the
              rates above, and said so here rather than guessed onto a row. */}
          {(summary.unlinked_ncr_refs?.length ?? 0) > 0 && (
            <p className="text-xs text-muted-foreground mt-3">
              Includes {formatQuantity(summary.unlinked_ncr_rejected)} rejected on{' '}
              {summary.unlinked_ncr_refs.map((r) => `NCR ${r.ncr_number}`).join(', ')}, not linked to a GRN above.
            </p>
          )}
        </div>
      )}
    </div>
  );
}
